// Shared presentation for the Palworld card collection binder: the nine-pocket
// grid, one pocket tile and the portfolio summary. Used by the member page
// (/collection) and by the public share page (/collection-share/$token), which
// renders exactly the same binder read-only.
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { OFFICIAL_CARD_BY_CODE, officialCardImage } from "@/lib/tcg-cards";
import { PRICE_SOURCES } from "@/lib/prices-shared";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { MarketPriceTag } from "@/components/MarketPrice";
import {
  COLLECTION_SLOTS_PER_PAGE,
  CONDITION_LABEL,
  effectiveSell,
  isSold,
  usd,
  type CollectionRow,
  type CollectionTotals,
  type Condition,
} from "@/lib/collection-shared";
import packFront from "@/assets/pack-front.png.asset.json";

/** Same faint pack-art placeholder the card binder uses for an empty pocket. */
function EmptySlotBackdrop() {
  return (
    <img
      src={packFront.url}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15 grayscale"
    />
  );
}


/** eBay links a collector actually uses when buying or listing a single card. */
const EBAY_IDS = ["ebay", "ebay-graded"] as const;

export function cardMeta(code: string) {
  const card = OFFICIAL_CARD_BY_CODE.get(code.toUpperCase());
  return {
    name: card?.name ?? code,
    rarity: card?.rarity ?? "PSD",
    set: card?.set ?? "",
    image: card?.image ?? officialCardImage(code),
    card,
  };
}

export function EbayLinks({ code, compact = false }: { code: string; compact?: boolean }) {
  const { card } = cardMeta(code);
  if (!card) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${compact ? "" : "mt-2"}`}>
      {EBAY_IDS.map((id) => {
        const source = PRICE_SOURCES.find((s) => s.id === id);
        if (!source) return null;
        return (
          <a
            key={id}
            href={source.card(card)}
            target="_blank"
            rel="nofollow ugc noopener noreferrer"
            onClick={() => trackAffiliateClick({ source: id, kind: "card", targetKey: card.code })}
            title={source.note}
            className="rounded border border-border/60 px-1.5 py-0.5 text-[10px] hover:border-primary/60"
          >
            {id === "ebay" ? "eBay asking" : "eBay graded"}
          </a>
        );
      })}
    </div>
  );
}

function profitOf(row: CollectionRow): number | null {
  const sell = effectiveSell(row);
  if (row.buy_price === null || sell === null) return null;
  return (sell - row.buy_price) * Math.max(1, row.quantity);
}

export function CollectionPocket({
  row,
  onEdit,
  onRemove,
  onDropCard,
  onToggleSold,
  draggable = false,
}: {
  row: CollectionRow;
  onEdit?: (row: CollectionRow) => void;
  onRemove?: (row: CollectionRow) => void;
  onDropCard?: (id: string, slot: number) => void;
  onToggleSold?: (row: CollectionRow) => void;
  draggable?: boolean;
}) {
  const meta = cardMeta(row.card_code);
  const profit = profitOf(row);
  const sold = isSold(row);
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", row.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className={`rounded-lg border p-1.5 ${sold ? "border-emerald-500/50 bg-emerald-500/5" : "border-border/50 bg-card/40"} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <div className="relative">
        <CardImage
          src={meta.image}
          alt={`${meta.name} ${row.card_code} Palworld card`}
          width={180}
          height={251}
          className={`w-full rounded-md ${sold ? "opacity-80" : ""}`}
        />
        {sold && (
          <span className="absolute left-1 top-1 rounded bg-emerald-500/90 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-background">
            Sold
          </span>
        )}
      </div>
      <p className="mt-1 truncate text-[11px] font-semibold" title={meta.name}>
        {meta.name}
      </p>
      <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
        <RarityBadge code={meta.rarity} />
        <span className="truncate">{row.card_code}</span>
      </p>
      <p className="mt-1 text-[10px] text-muted-foreground">
        {CONDITION_LABEL[(row.condition as Condition) ?? "raw"] ?? row.condition}
        {row.quantity > 1 ? ` · ×${row.quantity}` : ""}
      </p>
      <dl className="mt-1 space-y-0.5 text-[10px] tabular-nums">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Buy</dt>
          <dd>{usd(row.buy_price)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{sold ? "Sold for" : "Asking"}</dt>
          <dd className={sold ? "text-emerald-300" : ""}>{usd(effectiveSell(row))}</dd>
        </div>
        <div className="flex justify-between font-semibold">
          <dt className="text-muted-foreground">{sold ? "Banked" : "Paper P/L"}</dt>
          <dd className={profit === null ? "" : profit >= 0 ? "text-emerald-400" : "text-destructive"}>
            {profit === null ? "—" : usd(profit)}
          </dd>
        </div>
      </dl>
      {sold && row.sold_at && (
        <p className="mt-0.5 text-[9px] text-muted-foreground">Sold {row.sold_at.slice(0, 10)}</p>
      )}
      <div className="mt-1">
        <MarketPriceTag code={row.card_code} />
      </div>
      {(onEdit || onRemove || onToggleSold) && (
        <div className="mt-1 flex flex-wrap gap-2 text-[10px]">
          {onEdit && (
            <button type="button" onClick={() => onEdit(row)} className="text-primary hover:underline">
              Edit prices
            </button>
          )}
          {onToggleSold && (
            <button
              type="button"
              onClick={() => onToggleSold(row)}
              className={sold ? "text-muted-foreground hover:underline" : "text-emerald-400 hover:underline"}
            >
              {sold ? "Undo sale" : "Mark sold"}
            </button>
          )}
          {onRemove && (
            <button type="button" onClick={() => onRemove(row)} className="text-muted-foreground hover:text-destructive">
              Remove
            </button>
          )}
        </div>
      )}
      {onDropCard && <span className="sr-only">Pocket card {meta.name}</span>}
    </div>
  );
}


export function CollectionGrid({
  page,
  cards,
  onEdit,
  onRemove,
  onPickSlot,
  onDropCard,
  onToggleSold,
}: {
  page: number;
  cards: Map<number, CollectionRow>;
  onEdit?: (row: CollectionRow) => void;
  onRemove?: (row: CollectionRow) => void;
  onPickSlot?: (slot: number) => void;
  onDropCard?: (id: string, slot: number) => void;
  onToggleSold?: (row: CollectionRow) => void;
}) {
  const start = page * COLLECTION_SLOTS_PER_PAGE;
  const dropProps = (slot: number) =>
    onDropCard
      ? {
          onDragOver: (e: React.DragEvent) => e.preventDefault(),
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            const id = e.dataTransfer?.getData("text/plain");
            if (id) onDropCard(id, slot);
          },
        }
      : {};

  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      {Array.from({ length: COLLECTION_SLOTS_PER_PAGE }, (_, i) => {
        const slot = start + i;
        const row = cards.get(slot);
        return (
          <div key={slot} {...dropProps(slot)}>
            {row ? (
              <CollectionPocket
                row={row}
                onEdit={onEdit}
                onRemove={onRemove}
                onToggleSold={onToggleSold}
                draggable={Boolean(onDropCard)}
                onDropCard={onDropCard}
              />

            ) : onPickSlot ? (
              <button
                type="button"
                onClick={() => onPickSlot(slot)}
                aria-label={`Add a Palworld card to collection pocket ${slot + 1}`}
                className="relative flex aspect-[5/7] w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-primary/60 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:border-primary"
              >
                <EmptySlotBackdrop />
                <span className="relative z-10">Add to #{slot + 1}</span>
              </button>
            ) : (
              <div
                aria-label={`Empty collection pocket ${slot + 1}`}
                className="relative flex aspect-[5/7] w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-border/60 text-[10px] uppercase tracking-wide text-muted-foreground"
              >
                <EmptySlotBackdrop />
                <span className="relative z-10">#{slot + 1} empty</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function PortfolioSummary({ totals }: { totals: CollectionTotals }) {
  const realizedRoi = totals.realizedCost > 0 ? totals.realizedProfit / totals.realizedCost : null;
  const stats: { label: string; value: string; tone?: number }[] = [
    { label: "Cards tracked", value: `${totals.cards} (${totals.copies} copies)` },
    { label: "Total spent", value: usd(totals.invested) },
    { label: "Total sale value", value: usd(totals.valued) },
    { label: "Cards sold", value: `${totals.soldCards} · ${usd(totals.realized)}` },
    { label: "Banked profit", value: usd(totals.realizedProfit), tone: totals.realizedProfit },
    { label: "Paper profit", value: usd(totals.unrealizedProfit), tone: totals.unrealizedProfit },
    { label: "Realized ROI", value: realizedRoi === null ? "—" : `${(realizedRoi * 100).toFixed(1)}%`, tone: realizedRoi ?? 0 },
    { label: "Portfolio ROI", value: totals.roi === null ? "—" : `${(totals.roi * 100).toFixed(1)}%` },
  ];
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {stats.map(({ label, value, tone }) => (
        <div key={label} className="glass rounded-xl p-3">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
          <dd
            className={`mt-1 text-lg font-bold tabular-nums ${
              tone === undefined ? "" : tone >= 0 ? "text-emerald-400" : "text-destructive"
            }`}
          >
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

